import { useFormContext } from "react-hook-form";
import { z } from "zod";

import FormInput from "@/components/form-fields/form-input";
import { AsyncMultiSelect } from "@/components/reusables/async-select/async-multi-select";
import AdornmentInput from "@/components/ui/adornment-input";
import { CustomerStatus } from "@/constants/client.constants";
import { FIVE, TEN } from "@/constants/constants";
import useQueryParams from "@/hooks/useQueryParams";
import { IWorkForceUserEntity, RoleEnum } from "@/interfaces/globals";
import { customerUser } from "@/interfaces/interfaces";
import { renderFullName } from "@/lib/utils";
import { useGetAllCustomerByWSQuery } from "@/redux/api/clientQuery";
import { useGetAllWorforceUserQuery } from "@/redux/api/userQuery";
import { AddProjectSchema, MarkAsWonSchema } from "@/schemas/project.schema";

interface IProjectDetailsProps {
  isMarkAsWon?: boolean;
}
const ProjectsDetails = ({
  isMarkAsWon,
}: IProjectDetailsProps): React.ReactElement => {
  const { keys } = useQueryParams();
  const leadsId = keys.leadsId as string;
  const CurrentSchema = leadsId ? MarkAsWonSchema : AddProjectSchema;
  type TCurrentSchema = z.infer<typeof CurrentSchema>;

  const form = useFormContext<TCurrentSchema>();
  const { watch } = form;

  const customer = watch("customerId");

  return (
    <>
      <h2 className='col-span-3 text-lg font-medium'>Project Details</h2>
      <FormInput
        form={form}
        name='ourProjectTeam'
        label='Our Project Team'
        required
        render={(field) => (
          <AsyncMultiSelect<IWorkForceUserEntity, { role?: RoleEnum[] }>
            useQuery={useGetAllWorforceUserQuery}
            baseParams={{
              role: [RoleEnum.ADMIN, RoleEnum.MANAGER],
            }}
            label='Project Team'
            onChange={field.onChange}
            defaultLabels={form.getValues("defaultOurProjectTeam")}
            values={field.value}
            placeholder='Select managers'
            renderConfig={{
              valueKey: "id",
              labelRenderer: (user) =>
                renderFullName({
                  params: user,
                }),
            }}
          />
        )}
      />
      <FormInput
        form={form}
        name='customerProjectTeam'
        label='Customer Project Team'
        required
        render={(field) => (
          <AsyncMultiSelect<
            customerUser,
            { user?: string; status: string; customerId: string }
          >
            useQuery={useGetAllCustomerByWSQuery}
            baseParams={{
              status: CustomerStatus.ACTIVE,
              customerId: customer || "",
              user: "user",
            }}
            label='Project Team'
            disabled={!customer}
            onChange={field.onChange}
            defaultLabels={form.getValues("defaultCustomerProjectTeam")}
            values={field.value}
            placeholder='Select managers'
            renderConfig={{
              valueKey: "id",
              labelRenderer: (user) =>
                renderFullName({
                  params: user,
                }),
            }}
          />
        )}
      />
      <FormInput
        name='budgetHours'
        form={form}
        label='Budget Hours'
        charLimit={FIVE}
        input={{
          type: "number",
          placeholder: "Enter Budget Hours",
        }}
      />{" "}
      <FormInput
        name='customerProjectReference'
        form={form}
        label='Customer Project Number'
        charLimit={TEN}
        input={{
          placeholder: "Enter Customer Project Number",
        }}
      />
      {isMarkAsWon ? (
        <FormInput
          form={form}
          name='ourReferenceNumber'
          label='Our Reference Number'
          required
          render={(field) => {
            const prefix = field?.value?.endsWith("-")
              ? field?.value?.slice(0, -1) // Remove trailing "-"
              : field?.value?.substring(0, field?.value?.lastIndexOf("-"));
            const mutablePart = field?.value?.substring(
              field?.value?.lastIndexOf("-") + 1,
            );

            return (
              <AdornmentInput
                prefix={prefix}
                value={mutablePart ?? ""}
                placeholder='Enter Reference Number'
                onChange={(e) => {
                  //Check the char limit up to 10 digits
                  if (e.target.value.length > TEN) {
                    return;
                  }
                  // Prevent '-' character in the mutable part
                  const sanitizedValue = e.target.value.replace(/-/g, "");
                  field.onChange(
                    `${prefix ? `${prefix}-` : ""}${sanitizedValue}`,
                  );
                }}
              />
            );
          }}
        />
      ) : (
        <FormInput
          name='ourReferenceNumber'
          form={form}
          label='Our Reference Number'
          charLimit={TEN}
          input={{
            placeholder: "Enter Our Reference Number",
          }}
        />
      )}
    </>
  );
};

export default ProjectsDetails;
